import React, { useState } from 'react';
import { Loader2, Phone } from 'lucide-react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { toast } from 'sonner';
import { getAuthToken, updateUserData, getActivePortal } from '@/utils/auth';
import { cn } from '@/lib/utils';
import { PhoneInputWithCountry } from '@/components/ui/PhoneInputWithCountry';
import { validateE164Phone } from '@/constants/countryCodes';

export function ParentWhatsAppModal({ isOpen, onComplete, currentProfile }) {
  const [phoneNumber, setPhoneNumber] = useState(
    currentProfile?.parent_whatsapp_number || currentProfile?.phone_no || ''
  );
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!phoneNumber || !phoneNumber.trim()) {
      setErrorMsg("Please enter your parent's WhatsApp number.");
      return;
    }

    const valResult = validateE164Phone(phoneNumber);
    if (!valResult.valid) {
      setErrorMsg(valResult.error || "Please enter a valid phone number in international format.");
      return;
    }

    setLoading(true);
    try {
      const portal = getActivePortal() || 'student';
      const token = getAuthToken(portal);

      const payload = {
        phone_no: valResult.sanitized,
      };

      await axios.patch(apiUrl('users/profile/'), payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update cached user data
      updateUserData(portal, {
        phone_no: valResult.sanitized,
        parent_whatsapp_number: valResult.sanitized,
      });

      window.dispatchEvent(new CustomEvent('profileUpdated'));
      toast.success("Parent's WhatsApp number saved successfully!");
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Failed to save parent phone number:', err);
      const detail = err.response?.data?.error || err.response?.data?.detail || 'Failed to save phone number. Please try again.';
      setErrorMsg(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/70 backdrop-blur-md p-3 sm:p-4 select-none"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "w-full max-w-sm sm:max-w-md bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-2xl shadow-2xl p-6 sm:p-7 relative text-foreground",
          "animate-in fade-in zoom-in-95 duration-200"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-3 shadow-sm border border-emerald-500/20">
            <Phone className="w-6 h-6 text-emerald-500" />
          </div>
        </div>

        {errorMsg && (
          <div className="mb-4 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-xs text-left">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2 text-left">
            <label className="text-xs sm:text-sm font-semibold text-foreground block">
              Parent's WhatsApp Number <span className="text-emerald-500">*</span>
            </label>
            <PhoneInputWithCountry
              value={phoneNumber}
              onChange={(val) => {
                setPhoneNumber(val);
                if (errorMsg) setErrorMsg('');
              }}
              disabled={loading}
              placeholder="Enter mobile number"
              selectClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg text-foreground font-semibold py-2.5"
              inputClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground py-2.5"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1 pt-0.5">
              <span className="text-emerald-500 font-bold">*</span>
              <span>Receive important updates about your learning progress, tuition bookings and upcoming sessions.</span>
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 py-3 px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 border-0"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                <span>Saving...</span>
              </>
            ) : (
              'Save & Continue'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

